/**
 * Add a query to a URL.
 *
 * ## Notes
 *
 * This is a complex method with different behaviours depending on the exact input.
 * A trailing slash can change the outcome, as can an absolute vs relative URL.
 *
 * Please check the tests to verify if your result is as expected.
 *
 * - An absolute URL with no routes must end in a slash to use query parameters.
 *   This is not strictly necessary, and is a limitation of this function.
 * - Any URL with routes will end in a slash if the input URL does.
 *
 * @param {string} url the target URL
 * @param {Record<string, string>} query the query to add
 */
export function addQueryToUrl(url: string, query: Record<string, string>) {
    if (!query) {
        return url;
    }

    const isUrlAbsolute = url.startsWith('http');
    let urlObject: URL;
    if (isUrlAbsolute) {
        urlObject = new URL(url);
    } else {
        // Base URL does not matter as it will be removed in this case, but this is probably the best way to do it.

        urlObject = new URL(url, window.location.origin);
    }

    Object.entries(query).forEach(([key, value]) => urlObject.searchParams.set(key, value));

    if (isUrlAbsolute) {
        return urlObject.toString();
    }

    return urlObject.pathname + urlObject.search;
}
